import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { cancel } from 'src/backend';

const useCancellationReason = () => {
  const { t, i18n } = useTranslation('translation');
  const reasonsKeys = useMemo(
    () => ({
      'Receiver unable pick up': t(
        'donations.cancelation-reason.Receiver unable pick up'
      ),
      'Pick up attempted, no food available': t(
        'donations.cancelation-reason.Pick up attempted, no food available'
      ),
      'Donor location closed': t(
        'donations.cancelation-reason.Donor location closed'
      ),
      'Receiver location closed': t(
        'donations.cancelation-reason.Receiver location closed'
      ),
      'Schedule incorrect': t(
        'donations.cancelation-reason.Schedule incorrect'
      ),
      Other: t('donations.cancelation-reason.Other'),
    }),
    [t]
  );

  const getReason = useCallback(
    (key) => {
      const reason = reasonsKeys[key];
      if (!reason) {
        Sentry.captureMessage(`Unknown cancelation reason: ${key}`);
        return key;
      }
      return reason;
    },
    [reasonsKeys]
  );

  const exists = useCallback(
    (reason) => {
      return i18n.exists(`donations.cancelation-reason.${reason}`, {
        ns: 'translation',
      });
    },
    [i18n]
  );

  return {
    getReason,
    exists,
  };
};

export const useCancellationReasonOptions = ({ open }) => {
  const { t } = useTranslation('translation');
  const { enqueueSnackbar } = useSnackbar();
  const { getReason } = useCancellationReason();
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;
    const getReasonOptions = (data) =>
      [
        ...(Array.isArray(data) ? data : []),
        { id: 'custom', reason: 'Other' },
      ].map((item) => ({
        ...item,
        reason: getReason(item.reason),
      }));

    cancel
      .fetchCancellationReasons()
      .then((data) => {
        if (!isActive) return;
        setReasons(getReasonOptions(data));
      })
      .catch(() => {
        if (!isActive) return;
        enqueueSnackbar(t('form.errors.generic'), { variant: 'error' });
        setReasons(getReasonOptions([]));
      });

    return () => {
      isActive = false;
    };
  }, [enqueueSnackbar, getReason, open, t]);

  return reasons;
};

export default useCancellationReason;
